import { yupResolver } from "@hookform/resolvers/yup"
import { createAccountManuallyFormData } from "./CreateAccountManuallyForm.data"
import { CreateAccountManuallyFormMutation, CreateAccountManuallyFormSchema } from "./CreateAccountManuallyForm.types"
import { useForm, Controller } from "react-hook-form"
import { getCreateAccountManuallySchema } from "./CreateAccountManuallyForm.schema"
import { useMutation, useQueryClient } from "react-query"
import { CREATE_ACCOUNT_MANUALLY_QUERY_KEY, createAccountManuallyQuery } from "./CreateAccountManuallyForm.query"
import { CreateAccountManuallyRequestData } from "../../../../../../../../types/api/endpoints/prospect/create.types"
import { usePlansENT } from "../../../../../../../../hooks/fetchers/usePlansENT/usePlansENT"
import Loading from "../../../../../../../commons/Loading/Loading"
import { ChangeEvent, useContext, useMemo, useState } from "react"
import { AddressSuggestion } from "../../../../../../../../types/models/address/address.types"
import departements from "../../../../../../../../assets/json/departements-region.json"
import { useAddressAutocomplete } from "../../../../../../../../hooks/fetchers/useAddressAutocomplete/useAddressAutocomplete"
import SearchInput from "../../../../../../../commons/SearchInput/SearchInput"
import Input from "../../../../../../../commons/Input/Input"
import Select from "../../../../../../../commons/Select/Select"
import Button from "../../../../../../../designSystem/Button/Button"
import { ButtonSize } from "../../../../../../../designSystem/Button/Button.types"
import { DashboardProspectsMutation, UpdateStatusProspectWithCommentQuery } from "../../UpdateStatusProspectModal/UpdateStatusProspectForm/UpdateStatusProspectForm.types"
import { UPDATE_PROSPECT_STATUS_WITH_COMMENT_QUERY_KEY, updateStatusProspectWithCommentQuery } from "../../UpdateStatusProspectModal/UpdateStatusProspectForm/UpdateStatusProspectForm.query"
import { useNavigate } from "react-router-dom"
import { ProspectStatus } from "../../../../../../../../types/api/endpoints/prospect/prospect.types"
import { CreateAccountManuallyStepsContext } from "../../CreateAccountManuallySteps/CreateAccountManuallySteps.context"
import { StepWizardContext } from "../../../../../../../commons/StepWizard/StepWizard.context"

export default () => {

  const queryClient = useQueryClient();

  const navigate = useNavigate();

  const { prospectId, setAccountInfos } = useContext(CreateAccountManuallyStepsContext);

  const { setCurrentStep } = useContext(StepWizardContext);

  const [searchInputValue, setSearchInputValue] = useState<string>('');

  const { data: plansENT, isLoading } = usePlansENT();

  const { data: addressSuggestions } = useAddressAutocomplete(searchInputValue);

  const formProps = useForm<CreateAccountManuallyFormSchema>({
    resolver: yupResolver(getCreateAccountManuallySchema),
    mode: 'onChange',
    reValidateMode: 'onChange'
  });

  const { mutate: updateProspect } = useMutation<
    Awaited<ReturnType<DashboardProspectsMutation>>,
    Error,
    UpdateStatusProspectWithCommentQuery
  >((data) => updateStatusProspectWithCommentQuery(data), {
    mutationKey: UPDATE_PROSPECT_STATUS_WITH_COMMENT_QUERY_KEY,
    onMutate: () => queryClient.cancelMutations(),
    onSuccess: () => {
      navigate(0);
    },
    onError: (error) => {
      alert(error.message);
    },
  });

  const { mutate: updateProspectAndPay } = useMutation<
    Awaited<ReturnType<DashboardProspectsMutation>>,
    Error,
    UpdateStatusProspectWithCommentQuery
  >((data) => updateStatusProspectWithCommentQuery(data), {
    mutationKey: UPDATE_PROSPECT_STATUS_WITH_COMMENT_QUERY_KEY,
    onMutate: () => queryClient.cancelMutations(),
    onSuccess: () => {
      setCurrentStep(2)
    },
    onError: (error) => {
      alert(error.message);
    },
  });

  const {
    handleSubmit,
    getValues,
    setValue,
    control,
    formState: { isValid, errors },
  } = formProps;

  const { mutate: submitForm, isLoading: isSubmitLoading } = useMutation<
    Awaited<ReturnType<CreateAccountManuallyFormMutation>>,
    Error,
    CreateAccountManuallyRequestData
  >((data) => createAccountManuallyQuery(data), {
    mutationKey: CREATE_ACCOUNT_MANUALLY_QUERY_KEY,
    onMutate: () => queryClient.cancelMutations(),
    onSuccess: () => {
      updateProspect({
        id: prospectId,
        email: getValues('email'),
        status: ProspectStatus.AdhésionEnCours,
        comment: 'Compte créé manuellement',
        is_updated_automatically: true
      })
    },
    onError: async (error) => {
      alert(error.message);
    },
  });

  const { mutate: submitFormAndPay } = useMutation<
    Awaited<ReturnType<CreateAccountManuallyFormMutation>>,
    Error,
    CreateAccountManuallyRequestData
  >((data) => createAccountManuallyQuery(data), {
    mutationKey: CREATE_ACCOUNT_MANUALLY_QUERY_KEY,
    onMutate: () => queryClient.cancelMutations(),
    onSuccess: () => {
      setAccountInfos(getValues())
      updateProspectAndPay({
        id: prospectId,
        email: getValues('email'),
        status: ProspectStatus.AdhésionEnCours,
        comment: 'Compte créé manuellement',
        is_updated_automatically: true
      })
    },
    onError: async (error) => {
      alert(error.message);
    },
  });

  const onAddressChange = (
    e: ChangeEvent<HTMLInputElement>,
    onChange: (e: ChangeEvent<HTMLInputElement>) => void
  ) => {
    const value = e.target.value;
    onChange(e);
    setSearchInputValue(value);
  };

  const handleSelectedSuggestion = (suggestion: AddressSuggestion) => {
    setSearchInputValue('');
    setValue('address', suggestion.address);
    setValue('city', suggestion.city);
    setValue('postalCode', suggestion.postalCode);
    setValue('department', suggestion.department);
    setValue(
      'region',
      departements.find((res) => res.num_dep.toString() == suggestion.department_code)
        ?.region_name || ''
    );
    if (suggestion.coordinates) {
      setValue('latitude', suggestion.coordinates.latitude)
      setValue('longitude', suggestion.coordinates.longitude)
    }
  };

  const onSubmit = handleSubmit((data) => submitForm({ prospectId, ...data }));

  const onSubmitAndPay = handleSubmit((data) => submitFormAndPay({ prospectId, ...data }));

  const updatedFormData = useMemo(
    () =>
      createAccountManuallyFormData.map((row) =>
        row.map((item) => {
          switch (item.name) {
            case 'plan':
              if (plansENT) {
                return {
                  ...item,
                  options: plansENT.map(({ id, label }) => ({
                    value: id.toString(),
                    label,
                  })),
                };
              } else {
                return item;
              }
            default:
              return item;
          }
        })
      ),
    [plansENT]
  );

  if (isLoading) return <Loading />

  return (
    <form className="form form--scrollable">
      {updatedFormData.map((row, rowIndex) => (
        <div key={rowIndex} className="form__row">
          {row.map(({ name, placeholder, label, type, options, ...props }, inputIndex) => (
            <Controller
              key={inputIndex}
              name={name}
              render={({ field: { onChange, value, ...rest } }) =>
                name === 'address' ? (
                  <SearchInput
                    suggestions={addressSuggestions}
                    placeholder={placeholder || ''}
                    type={type}
                    onChange={(e) => onAddressChange(e, onChange)}
                    error={errors[name]?.message as string}
                    // @ts-ignore
                    value={value}
                    {...{ label, handleSelectedSuggestion, name }}
                    {...props}
                  />
                ) : type !== 'select' ? (
                  <Input
                    placeholder={placeholder || ''}
                    type={type}
                    disabled={Boolean(
                      ['department', 'region', 'city', 'postalCode'].includes(name)
                    )}
                    error={errors[name]?.message as string}
                    // @ts-ignore
                    value={value}
                    {...{ onChange, label, name }}
                    {...props}
                  />
                ) : (
                  <Select
                    placeholder={placeholder || ''}
                    error={errors[name]?.message as string}
                    {...{ label, options, ...rest }}
                    {...props}
                    // @ts-ignore
                    onChange={({ value }: SelectOption) => {
                      onChange(value);
                    }}
                  />
                )
              }
              {...{ control }}
            />
          ))}
        </div>
      ))}
      <Button
        style={{ alignSelf: 'unset', marginBottom: 'var(--spacing-sToM)' }}
        onClick={(e) => {
          e.preventDefault();
          onSubmit();
        }}
        size={ButtonSize.Large}
        isLoading={isSubmitLoading}
        disabled={!isValid}>
        Valider
      </Button>
      <Button
        style={{ alignSelf: 'unset', marginBottom: 'var(--spacing-sToM)', display: 'none' }}
        onClick={(e) => {
          e.preventDefault();
          onSubmitAndPay();
        }}
        size={ButtonSize.Large}
        disabled={!isValid}>
        Valider et payer
      </Button>
      {/* <Button onClick={() => setCurrentStep(2)}>
        Open payment form
      </Button> */}
    </form>
  )
}